import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { FunctionComponent, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import RoundedButton from 'components/RoundedButton'
import CalendarIcon from 'components/SVG/Calendar'
import Spacer from 'components/Spacer'

import { isClosedOrSold } from 'utils/statusHelpers'
import { calculateTimeWithTimezone, getTimeZoneAbbreviation } from 'utils/timezone'

import * as T from 'types'

dayjs.extend(isSameOrAfter)

const Container = styled.div`
  padding: 20px;
  background: ${props => props.theme.colors.grey2};
  display: flex;
  flex-direction: column;
`

const StyledHeader = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 32px;
  line-height: 62px;
  color: ${props => props.theme.colors.black};
`

const DateRow = styled.div`
  display: flex;
  align-items: center;
`

const DateColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const DateText = styled(StyledHeader)`
  font-size: 16px;
  line-height: 22px;
`

interface IProps {
  listing: T.IListing
}

const OpenHouse: FunctionComponent<IProps> = ({ listing }) => {
  const theme = useContext(ThemeContext)

  const filteredDates = (listing?.openHouseDates ?? []).filter(
    date => !date.cancelled && dayjs(date.end).isSameOrAfter(dayjs()),
  )

  if (isClosedOrSold(listing) || !filteredDates.length) return null

  return (
    <Container>
      <StyledHeader>Open House</StyledHeader>
      <Spacer size={20} />
      {filteredDates.map((date, idx) => {
        const day = calculateTimeWithTimezone(date.start, listing.timeZone, 'dddd, MMM Do')
        const startTime = calculateTimeWithTimezone(date.start, listing.timeZone, 'h:mm')
        const endTime = calculateTimeWithTimezone(date.end, listing.timeZone, 'h:mm')
        const timeZone = getTimeZoneAbbreviation(listing, new Date(date.end))
        const virtualText = date?.virtual ? ' (Virtual Only)' : ''
        return (
          <>
            <DateRow>
              <CalendarIcon
                width={38}
                height={40}
                fill={theme.colors.black}
              />
              <Spacer size={16} />
              <DateColumn>
                <DateText>{day}</DateText>
                <DateText>{`${startTime} - ${endTime} ${timeZone}${virtualText}`}</DateText>
                {!!date.virtual && (
                  <RoundedButton
                    background={theme.colors.purple}
                    borderWidth={1}
                    color={theme.colors.white}
                    height={22}
                    href={date.virtualUrl}
                    text="Link"
                    bold
                    compactText
                    padding="4px 12px"
                  />
                )}
              </DateColumn>
            </DateRow>
            {idx < filteredDates.length - 1 && <Spacer size={15} />}
          </>
        )
      })}
    </Container>
  )
}

export default OpenHouse
